import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, Package, DollarSign, FileText, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPrograms = () => {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProgram, setEditingProgram] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    program_id: '',
    name: '',
    level: 'nivel_1',
    price: 0,
    original_price: '',
    description: '',
    payment_type: 'one_time',
    features: [''],
    is_active: true
  });
  const token = localStorage.getItem('session_token');

  useEffect(() => {
    fetchPrograms();
  }, []);

  const fetchPrograms = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/programs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPrograms(response.data || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...formData,
        features: formData.features.filter(f => f.trim() !== '')
      };

      if (editingProgram) {
        await axios.put(`${BACKEND_URL}/api/admin/programs/${editingProgram}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Programa actualizado');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/programs`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Programa creado');
      }
      
      setShowForm(false);
      setEditingProgram(null);
      resetForm();
      fetchPrograms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleEdit = (program) => {
    setFormData({
      program_id: program.program_id || program.packageId || '',
      name: program.name || program.title || '',
      level: program.level || 'nivel_1',
      price: program.price || 0,
      original_price: program.original_price || '',
      description: program.description || '',
      // No asumir 'annual' si el campo viene vacio: eso sobrescribia programas
      // Premium de pago unico con 'annual' con solo abrir Editar y Guardar.
      payment_type: program.payment_type || 'one_time',
      features: program.features?.length > 0 ? program.features : [''],
      is_active: program.is_active !== false
    });
    setEditingProgram(program.program_id || program.packageId);
    setShowForm(true);
  };

  const handleDelete = async (programId) => {
    if (!window.confirm('¿Eliminar este programa?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/programs/${programId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Programa eliminado');
      fetchPrograms();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const resetForm = () => {
    setFormData({
      program_id: '',
      name: '',
      level: 'nivel_1',
      price: 0,
      original_price: '',
      description: '',
      payment_type: 'one_time',
      features: [''],
      is_active: true
    });
  };

  const addFeature = () => {
    setFormData({ ...formData, features: [...formData.features, ''] });
  };

  const updateFeature = (index, value) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    setFormData({ ...formData, features: newFeatures });
  };

  const removeFeature = (index) => {
    const newFeatures = formData.features.filter((_, i) => i !== index);
    setFormData({ ...formData, features: newFeatures.length > 0 ? newFeatures : [''] });
  };

  const levelOptions = [
    { value: 'nivel_1', label: 'Nivel 1 - Principiante' },
    { value: 'nivel_2', label: 'Nivel 2 - Intermedio' },
    { value: 'nivel_3', label: 'Nivel 3 - Avanzado' },
    { value: 'premium', label: 'Premium' }
  ];

  // Programas por defecto si no hay en la BD
  const defaultPrograms = [
    {
      program_id: 'nivel_1',
      name: 'Booty and Legs Lab',
      level: 'nivel_1',
      price: 39.00,
      payment_type: 'one_time',
      description: 'Fundamentos y técnica básica para comenzar tu transformación',
      features: [
        '4 días de entrenamiento en video',
        'Plan de nutrición personalizado (formulario)',
        'Acceso a biblioteca educativa (contenido limitado)',
        'Pago único - Acceso de por vida'
      ]
    },
    {
      program_id: 'nivel_2',
      name: 'Booty and Legs Evolution',
      level: 'nivel_2',
      price: 59.00,
      payment_type: 'one_time',
      description: 'Mayor intensidad y variedad para llevar tu entrenamiento al siguiente nivel',
      features: [
        '5 días de entrenamiento en video',
        'Plan nutricional con 5 menús diferentes',
        'Registro y seguimiento de entrenamiento',
        'Más contenido educativo desbloqueado',
        'Ebook de nutrición y entrenamiento básico (PDF)',
        'Pago único - Acceso de por vida'
      ]
    },
    {
      program_id: 'premium',
      name: 'Plan Premium',
      level: 'premium',
      price: 199.00,
      payment_type: 'annual',
      description: 'Acceso completo a todo el contenido de la plataforma',
      features: [
        'Acceso a TODOS los niveles de entrenamiento',
        '6 días de entrenamiento en video avanzado',
        'Nutrición especializada: 3 menús masa muscular + 3 reducir grasa',
        'Biblioteca educativa completa',
        'Todos los Ebooks incluidos (PDF)',
        'Acceso a conversatorios exclusivos',
        'Acceso gratuito a TODOS los challenges'
      ]
    }
  ];

  const displayPrograms = programs.length > 0 ? programs : defaultPrograms;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <Package className="w-6 h-6 text-pink-500" />
            Gestión de Programas
          </h2>
          <p className="text-zinc-400 text-sm mt-1">Administra los planes de suscripción y sus precios</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setShowForm(true); setEditingProgram(null); }}
          className="bg-pink-600 hover:bg-pink-500 text-white rounded-full"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Programa
        </Button>
      </div>

      {/* Formulario */}
      {showForm && (
        <div className="bg-zinc-800 border border-zinc-700 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-white">
              {editingProgram ? 'Editar Programa' : 'Crear Nuevo Programa'}
            </h3>
            <button onClick={() => { setShowForm(false); setEditingProgram(null); }} className="text-zinc-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">ID del Programa</label>
                {/* El backend genera este ID (prog_xxxxxxxx) y descarta cualquier
                    valor enviado al crear, asi que el campo se muestra solo de
                    lectura en vez de pedirlo como obligatorio. */}
                <Input
                  value={editingProgram ? formData.program_id : ''}
                  placeholder="Se genera automáticamente"
                  className="bg-zinc-900 border-zinc-700 text-zinc-400"
                  readOnly
                  disabled
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Nombre del Programa</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="ej: Booty and Legs Lab"
                  className="bg-zinc-900 border-zinc-700 text-white"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Nivel</label>
                <select
                  value={formData.level}
                  onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                  className="w-full px-4 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white"
                >
                  {levelOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Precio (USD)</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-2.5 w-4 h-4 text-zinc-500" />
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                    className="pl-9 bg-zinc-900 border-zinc-700 text-white"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">
                  Precio Gancho (USD) 
                  <span className="text-zinc-500 text-xs ml-1">(se muestra tachado)</span>
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-2.5 w-4 h-4 text-zinc-500" />
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.original_price}
                    onChange={(e) => setFormData({ ...formData, original_price: e.target.value ? parseFloat(e.target.value) : '' })}
                    className="pl-9 bg-zinc-900 border-zinc-700 text-white"
                    placeholder="Ej: 79 (opcional)"
                  />
                </div>
                <p className="text-xs text-zinc-500 mt-1">Deja vacío si no quieres mostrar precio tachado</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Tipo de Pago</label>
                <select
                  value={formData.payment_type}
                  onChange={(e) => setFormData({ ...formData, payment_type: e.target.value })}
                  className="w-full px-4 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white"
                >
                  <option value="one_time">Pago único (de por vida)</option>
                  <option value="annual">Pago anual</option>
                  <option value="monthly">Pago mensual</option>
                </select>
              </div>
              <label className="flex items-center gap-2 mt-3 md:col-span-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_active !== false}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4"
                  data-testid="program-is-active-toggle"
                />
                <span className="text-sm text-zinc-300">
                  Activo (visible en la web pública y checkout)
                </span>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">Descripción</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe lo que incluye este programa..."
                rows={3}
                className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-lg text-white resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">Características</label>
              <div className="space-y-2">
                {formData.features.map((feature, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={feature}
                      onChange={(e) => updateFeature(index, e.target.value)}
                      placeholder={`Característica ${index + 1}`}
                      className="flex-1 bg-zinc-900 border-zinc-700 text-white"
                    />
                    <button
                      type="button"
                      onClick={() => removeFeature(index)}
                      className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addFeature}
                  className="text-sm text-pink-400 hover:text-pink-300 flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" /> Agregar característica
                </button>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="submit" className="bg-pink-600 hover:bg-pink-500 text-white rounded-full flex-1">
                <Save className="w-4 h-4 mr-2" />
                {editingProgram ? 'Guardar Cambios' : 'Crear Programa'}
              </Button>
              <Button type="button" onClick={() => { setShowForm(false); setEditingProgram(null); }} variant="outline" className="rounded-full border-zinc-700 text-zinc-300">
                Cancelar
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Lista de Programas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {displayPrograms.map((program) => {
          const isPremium = program.level === 'premium';
          return (
            <div 
              key={program.program_id || program.packageId}
              className={`bg-zinc-800 border rounded-2xl overflow-hidden ${
                isPremium ? 'border-pink-500' : 'border-zinc-700'
              }`}
            >
              {isPremium && (
                <div className="bg-pink-500 text-white text-center py-2 text-sm font-bold">
                  ⭐ PREMIUM
                </div>
              )}
              
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-pink-400">
                      {program.level?.replace('_', ' ')}
                    </span>
                    {program.is_active === false && (
                      <span className="ml-2 text-xs bg-zinc-700 text-zinc-300 px-2 py-0.5 rounded-full align-middle" data-testid={`program-badge-inactive-${program.program_id || program.packageId}`}>
                        Inactivo
                      </span>
                    )}
                    <h3 className="text-xl font-bold text-white mt-1">{program.name || program.title}</h3>
                  </div>
                  <div className="text-right">
                    {program.original_price && program.original_price > program.price && (
                      <p className="text-lg text-zinc-500 line-through">${program.original_price}</p>
                    )}
                    <p className="text-2xl font-bold text-white">${program.price}</p>
                    <p className="text-xs text-zinc-500">
                      {program.payment_type === 'annual' ? 'Anual' : 
                       program.payment_type === 'monthly' ? 'Mensual' : 'Único'}
                    </p>
                  </div>
                </div>

                <p className="text-zinc-400 text-sm mb-4">{program.description}</p>

                {program.features && (
                  <ul className="space-y-2 mb-6">
                    {program.features.slice(0, 4).map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-zinc-300">
                        <Check className="w-4 h-4 text-pink-500 flex-shrink-0 mt-0.5" />
                        <span className="line-clamp-1">{feature}</span>
                      </li>
                    ))}
                    {program.features.length > 4 && (
                      <li className="text-xs text-zinc-500">+{program.features.length - 4} más...</li>
                    )}
                  </ul>
                )}

                <div className="flex gap-2">
                  <Button
                    onClick={() => handleEdit(program)}
                    className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white rounded-full"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Editar
                  </Button>
                  <Button
                    onClick={() => handleDelete(program.program_id || program.packageId)}
                    variant="outline"
                    className="border-red-500/50 text-red-400 hover:bg-red-500/10 rounded-full"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Info */}
      <div className="bg-zinc-800/50 border border-zinc-700 rounded-2xl p-6">
        <h4 className="font-bold text-white mb-2 flex items-center gap-2">
          <FileText className="w-5 h-5 text-pink-500" />
          Información
        </h4>
        <p className="text-zinc-400 text-sm">
          Los cambios en los programas se reflejarán inmediatamente en la página de Programas. 
          Los precios están en USD y serán convertidos a COP automáticamente para pagos con Wompi.
        </p>
      </div>
    </div>
  );
};

export default AdminPrograms;
